import React, { useCallback, useEffect, useReducer, useRef, memo } from "react";
import { has } from "lodash";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { navigate } from "gatsby";

import Autocomplete from "~/components/Autocomplete";
import BaseModal from "~/components/BaseModal";
import IconOnlyButton from "~/components/buttons/IconOnlyButton";

import { h2 } from "~/styles/typographyStyles";

import { breakpointNumbers } from "~/theme";

import trackNavigationEvent from "~/utilities/trackNavigationEvent";

const Text = styled.h1`
    ${h2};
    text-align: center;
    color: ${({ theme }) => theme.palette.white};
    margin-bottom: 4rem;
    font-size: 3rem;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        font-size: 4rem;
    }
`;

const CloseButton = styled(IconOnlyButton)`
    position: fixed;
    color: ${({ theme }) => theme.palette.white};
    border: 2px solid transparent;

    &:focus,
    &:focus-visible,
    &:hover {
        border: 2px solid ${({ theme }) => theme.palette.white};
        color: ${({ theme }) => theme.palette.focusBlue};
    }
`;

const CrossButton = styled(CloseButton)`
    right: 3%;
    display: none;
    font-size: 8rem;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        display: block;
    }
`;

const LeftArrowButton = styled(CloseButton)`
    top: 4%;
    left: 5%;
    display: block;
    font-size: 3rem;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        display: none;
    }
`;

const ModalContent = styled.div`
    max-width: 1000px;
    margin: 10rem auto 0 auto;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        margin: 14rem auto 0 auto;
    }
`;

const SearchButton = styled(IconOnlyButton)`
    color: ${({ theme }) => theme.colors.typography.defaultLightBackground};

    @media (max-width: ${({ theme }) => theme.breakpoints.desktopMax}) {
        margin-right: 25px;
        font-size: 2.2rem;
    }
`;

const DynamicButton = styled.div`
    display: none;
`;

const initialState = {
    doc: null,
    isModalOpen: false,
    wasModalClosed: false,
};

const reducer = (state, action) => {
    switch (action.type) {
        case "SET_DOC":
            return {
                ...state,
                doc: action.doc,
                isModalOpen: false,
                wasModalClosed: true,
            };
        case "OPEN_MODAL":
            return {
                ...state,
                isModalOpen: true,
                wasModalClosed: false,
            };
        case "CLOSE_MODAL":
            return {
                ...state,
                isModalOpen: false,
                wasModalClosed: true,
            };
        case "CLEAR_STATE":
            return initialState;
        default:
            return state;
    }
};

const HeaderSearch = memo(function HeaderSearch({
    searchPlaceholder = "Search Wolfspeed",
    searchLabel = "What are you looking for?",
}) {
    const dynamicButtonRef = useRef();
    const openModalButtonRef = useRef();
    const [state, dispatch] = useReducer(reducer, initialState);
    const { doc, isModalOpen, wasModalClosed } = state;

    const search = (item) => {
        if (has(item, "doc")) {
            dispatch({ type: "SET_DOC", doc: item.doc });
        } else {
            navigate(`/search?q=${item.title}&page=1&size=25`, {
                state: { isHeaderNav: true },
            });
        }
    };

    const clearParentState = useCallback(() => {
        dispatch({ type: "CLEAR_STATE" });
    }, []);

    const closeModal = () => {
        dispatch({ type: "CLOSE_MODAL" });
    };

    useEffect(() => {
        if (dynamicButtonRef.current && doc) {
            const element = dynamicButtonRef.current.querySelector("button, a");
            if (element) {
                element.click();
                if (element.getAttribute("target") === "_blank") {
                    clearParentState();
                }
            } else {
                clearParentState();
            }
        }
    }, [doc, clearParentState]);

    useEffect(() => {
        // Prevents Safari from scrolling to the bottom of the page when modal is closed
        if (wasModalClosed && openModalButtonRef.current) {
            openModalButtonRef.current.focus();
        }
    }, [wasModalClosed]);

    useEffect(() => {
        if (isModalOpen) {
            const isDesktop =
                window.innerWidth > breakpointNumbers.tabletLandscapeMax;
            trackNavigationEvent(false, 1, "Search", isDesktop);
        }
    }, [isModalOpen]);

    return (
        <>
            <SearchButton
                onClick={() => dispatch({ type: "OPEN_MODAL" })}
                screenReaderText="Search"
                iconLibrary="fas"
                icon="search"
                tagRef={openModalButtonRef}
            />
            <BaseModal
                {...{
                    isOpen: isModalOpen,
                    showIconButton: false,
                    overlayStyles: { backgroundColor: "rgba(0, 0, 0, .8)" },
                    contentStyles: {
                        maxWidth: "100%",
                        minHeight: "100%",
                        background: "none",
                        border: "none",
                        padding: "0",
                    },
                }}
            >
                <CrossButton
                    onClick={closeModal}
                    screenReaderText="Click to close"
                    iconLibrary="fas"
                    icon="times"
                />
                <LeftArrowButton
                    onClick={closeModal}
                    screenReaderText="Click to close"
                    iconLibrary="fas"
                    icon="arrow-left"
                />
                <ModalContent>
                    <Text>{searchLabel}</Text>
                    <Autocomplete
                        id="global-search"
                        label={searchPlaceholder}
                        placeholder={searchPlaceholder}
                        isModal={true}
                        srOnly={true}
                        updateParent={search}
                        closeModal={closeModal}
                    />
                </ModalContent>
            </BaseModal>
            <DynamicButton ref={dynamicButtonRef}>
                {doc ? doc.getSearchButton(clearParentState) : null}
            </DynamicButton>
        </>
    );
});

HeaderSearch.propTypes = {
    searchPlaceholder: PropTypes.string,
    searchLabel: PropTypes.string,
};

export default HeaderSearch;
